import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  Layers,
  MapPin,
  DollarSign,
  Users,
  MessageSquare,
  FileText,
  ClipboardList,
  Settings,
  BarChart2,
  Menu, // 🍔 Import Menu icon for the hamburger
  X,      // ❌ Import X icon for the close button
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../css/Sidebar.css";
import { LogoutButton } from "../../../components/Super Admin/NavBar/NavBar";

// Interfaces and sections array remain the same...
interface SidebarItem {
  label: string;
  icon?: React.ReactNode;
  path: string;
  disabled?: boolean;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

const sections: SidebarSection[] = [

  {
    title: "Operations",
    items: [
      { label: "Core Functions", icon: <Layers size={16} />, path: "/forms" },
      { label: "Center Management", icon: <MapPin size={16} />, path: "/release-appointment" },
    ],
  },
  {
    title: "Marketing Functions",
    items: [
      { label: "Pricing Management", icon: <DollarSign size={16} />, path: "/fee-tax" },
      { label: "User Engagement", icon: <Users size={16} />, path: "/parent-list" },
      { label: "Helpdesk Management", icon: <MessageSquare size={16} />, path: "/helpdesk" },
    ],
  },
  {
    title: "Clinical Functions",
    items: [
      { label: "Questionnaire Management", icon: <ClipboardList size={16} />, path: "/questionnaire" },
      { label: "EMR Management", icon: <FileText size={16} />, path: "/emr-management"},
    ],
  },

    {
    title: "Dashboard",
    items: [
      { label: "Center Dashboard", icon: <BarChart2 size={16} />, path: "/center-dashboard" },
    ],
  },
  // {
  //   title: "Process Management",
  //   items: [
  //     { label: "Forms Management", icon: <FileText size={16} />, path: "/forms-management", disabled: true },
  //   ],
  // },
  // {
  //   title: "Systems Functions",
  //   items: [
  //     { label: "Systems Management", icon: <Settings size={16} />, path: "/systems", disabled: true },
  //   ],
  // },
  // {
  //   title: "Executive Functions",
  //   items: [
  //     { label: "Dashboard Monitoring", icon: <BarChart2 size={16} />, path: "/dashboard", disabled: true },
  //   ],
  // },
];


const Sidebar: React.FC = () => {
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [activeItem, setActiveItem] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false); // ✅ State for mobile nav

  const navigate = useNavigate();
  const location = useLocation();

  const getSidebarActivePath = (pathname: string): string => {
    const normalizedPath = pathname.replace(/\/?$/, "");

    const pathMap: Record<string, string> = {
      "/add-stations": "/release-appointment",
      "/edit-appointment": "/release-appointment",
      "/edit-log": "/release-appointment",
      "/coupons": "/fee-tax",
      "/refund": "/fee-tax",
      "/histories": "/questionnaire",
      "/roles": "/forms",
      "/stations": "/forms",
      "/centers": "/forms",
      "/users": "/forms",
      "/slots": "/forms",
    };

    return pathMap[normalizedPath] || normalizedPath;
  };

  // Effect to set the active item based on the current URL path
  useEffect(() => {
    const activePath = getSidebarActivePath(location.pathname);
    setActiveItem(activePath);

    // Auto-open the section that contains the active item (only if not already open)
    const activeSection = sections.find((section) =>
      section.items.some((item) => item.path === activePath)
    );
    if (activeSection) {
      setOpenSections((prev) =>
        prev.includes(activeSection.title) ? prev : [...prev, activeSection.title]
      );
    }
  }, [location.pathname]);

  // Effect to get displayName from session storage (your existing code)
  useEffect(() => {
    try {
      const backendResponse =
        sessionStorage.getItem("backendResponse") ||
        sessionStorage.getItem("BackendResponse");

      if (backendResponse) {
        const parsed = JSON.parse(backendResponse);
        if (parsed.displayName) {
          setDisplayName(parsed.displayName);
        }
      }
    } catch (error) {
      console.error("Error parsing backendResponse:", error);
    }
  }, []);

  const toggleSection = (title: string) => {
    setOpenSections((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    setActiveItem(path);
    setIsMobileNavOpen(false);
  };

  return (
    <>
      {/* 🍔 Hamburger Menu Button - visible only on mobile */}
      <button
        className="form-hamburger-menu"
        onClick={() => setIsMobileNavOpen(true)}
      >
        <Menu size={24} />
      </button>

      {/* 🌑 Overlay - visible when mobile nav is open */}
      {isMobileNavOpen && (
        <div
          className="form-sidebar-overlay"
          onClick={() => setIsMobileNavOpen(false)}
        ></div>
      )}

      {/* The actual sidebar */}
      <div className={`form-sidebar ${isMobileNavOpen ? "open" : ""}`}>
        <div className="form-sidebar-header">
          <h2 className="form-sidebar-title">
            {displayName ? `Hi ${displayName}` : "Hi User"}
          </h2>
          {/* ❌ Close Button - visible inside sidebar on mobile */}
          <button
            className="form-sidebar-close-btn"
            onClick={() => setIsMobileNavOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        {sections.map((section) => {
          // Section stays open if manually opened OR if it contains the currently active item
          const isOpen = openSections.includes(section.title) || section.items.some((item) => activeItem === item.path);

          return (
            <div key={section.title} className="form-sidebar-section">
              <button
                className="form-sidebar-section-header"
                onClick={() => toggleSection(section.title)}
              >
                <span>{section.title}</span>
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>

              {isOpen && (
                <ul className="form-sidebar-items">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <button
                        className={`form-sidebar-item ${
                          activeItem === item.path ? "active" : ""
                        }`}
                        onClick={() => handleItemClick(item.path)}
                        disabled={item.disabled}
                      >
                        <span className="form-sidebar-item-icon">{item.icon}</span>
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        <div className="form-sidebar-footer">
            <LogoutButton />
        </div>
      </div>
    </>
  );
};

export default Sidebar;